import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  InputAdornment,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  Badge,
  TableSortLabel,
  TablePagination,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import MailIcon from '@mui/icons-material/Mail';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockIcon from '@mui/icons-material/Lock';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { useUserContext } from '../../hooks/useUserContext';
import { getApiErrorInfo } from '../../services/api';
import { config } from '../../config';
import userService from '../../services/userService';
import tenantService from '../../services/tenantService';
import addonAssignmentService, { type AddonSeatSummary, ADDON_DISPLAY_NAMES } from '../../services/addonAssignmentService';
import tenantInvitationService, { type TenantInvitation } from '../../services/tenantInvitationService';
import { track } from '../../services/analytics';
import { subscriptionChipSx, type SubscriptionTone } from '../../utils/subscriptionStatus';
import AccessRequestsPanel from './AccessRequestsPanel';

interface User {
  id: string;
  tenantId: string;
  authProviderId?: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  createdAt: string;
  updatedAt: string;
  // Admin-enriched fields returned by the system-admin user listing endpoint
  userType?: string;
  subscriptionPlanName?: string;
  subscriptionStatus?: string;
  assignedAddonIds?: string[];
}

interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

type ModalMode = 'edit' | 'invite' | 'delete' | 'import' | 'move-tenant' | null;

interface CsvRow {
  firstName?: string;
  lastName?: string;
  email: string;
  role: string;
}

type ImportStatus = 'pending' | 'success' | 'error';

interface CsvImportRow extends CsvRow {
  status: ImportStatus;
  message?: string;
}

type TableEntry = { kind: 'user'; user: User } | { kind: 'invitation'; invitation: TenantInvitation };

// ── User-type helpers ────────────────────────────────────────────────────────

const USER_TYPE_LABELS: Record<string, string> = {
  'system-admin': 'System Admin',
  'tenant-admin': 'Tenant Admin',
  individual: 'Individual',
  'tenant-member': 'Tenant Member',
};

const USER_TYPE_COLORS: Record<string, 'warning' | 'primary' | 'success' | 'default'> = {
  'system-admin': 'warning',
  'tenant-admin': 'primary',
  individual: 'success',
  'tenant-member': 'default',
};

function resolveUserType(user: User, defaultTenantId: string): string {
  if (user.userType) return user.userType;
  if (user.role === 'admin') return 'system-admin';
  if (user.role === 'tenant-admin') return 'tenant-admin';
  return user.tenantId === defaultTenantId ? 'individual' : 'tenant-member';
}

// ── Subscription status helpers ───────────────────────────────────────────────

const SUBSCRIPTION_STATUS_MAP: Record<string, { label: string; tone: SubscriptionTone }> = {
  active: { label: 'Active', tone: 'success' },
  in_trial: { label: 'Trial', tone: 'info' },
  non_renewing: { label: 'Ending', tone: 'warning' },
  cancelled: { label: 'Cancelled', tone: 'error' },
  paused: { label: 'Paused', tone: 'neutral' },
};

type SortColumn = 'name' | 'role' | 'status' | 'created';
type SortDirection = 'asc' | 'desc';

export default function UserManagement() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAdmin: isSystemAdmin, backendProfile, loading: userLoading } = useUserContext();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'invited' | 'with-seats' | 'no-seats'>('all');
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'user',
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [seatSummary, setSeatSummary] = useState<AddonSeatSummary[]>([]);
  const [seatsActionLoading, setSeatsActionLoading] = useState(false);

  // Page-level tab
  const [activeTab, setActiveTab] = useState<'users' | 'requests'>('users');

  // Table sorting and pagination
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // System admin promotion confirmation
  const [showAdminConfirmDialog, setShowAdminConfirmDialog] = useState(false);

  // Move-to-tenant state (system admin only)
  const [tenants, setTenants] = useState<{ id: string; name: string }[]>([]);
  const [moveTab, setMoveTab] = useState<'existing' | 'new'>('existing');
  const [moveTenantId, setMoveTenantId] = useState('');
  const [moveRole, setMoveRole] = useState('user');
  const [newTenantName, setNewTenantName] = useState('');
  const [moveLoading, setMoveLoading] = useState(false);

  // CSV import state
  const [csvRows, setCsvRows] = useState<CsvImportRow[]>([]);
  const [csvImporting, setCsvImporting] = useState(false);

  // Pending cross-tenant invitations (app-managed, for users who already exist in another tenant)
  const [pendingTenantInvitations, setPendingTenantInvitations] = useState<TenantInvitation[]>([]);

  // Pending access request count (module access requests awaiting admin review)
  const [pendingAccessRequestCount, setPendingAccessRequestCount] = useState(0);

  const urlParams = new URLSearchParams(window.location.search);
  const queryTenantId = urlParams.get('tenantId');
  const isSystemAdminRoute = location.pathname.startsWith('/app/system-admin');
  const isSystemWideView = isSystemAdmin && isSystemAdminRoute && !queryTenantId;

  // Resolve tenant ID: explicit query param (system admin scoped view) or current user's tenant
  const currentTenantId = queryTenantId && isSystemAdmin
    ? queryTenantId
    : (backendProfile?.tenantId ?? null);

  // Users in the default tenant cannot be assigned the tenant-admin role
  const isDefaultTenant = !!currentTenantId && currentTenantId === config.defaultTenantId;

  useEffect(() => {
    if (!userLoading && (currentTenantId || isSystemWideView)) {
      fetchUsers();
      if (currentTenantId) {
        fetchPendingTenantInvitations();
        fetchPendingAccessRequestCount();
      } else {
        setPendingTenantInvitations([]);
        setPendingAccessRequestCount(0);
      }
    }
  }, [userLoading, currentTenantId, isSystemWideView]);

  useEffect(() => {
    if (isSystemAdmin) {
      tenantService.getAllTenants().then(setTenants).catch(() => {});
    }
  }, [isSystemAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isSystemWideView) {
        const data = await userService.getAllSystemUsers();
        setUsers(data);
        setSeatSummary([]);
        return;
      }

      if (!currentTenantId) return;

      const [data, summary] = await Promise.all([
        userService.getAllUsers(currentTenantId),
        addonAssignmentService.getSeatSummary(currentTenantId).catch(() => []),
      ]);
      setUsers(data);
      setSeatSummary(summary);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('Error fetching users:', error);
      setError(apiError.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingTenantInvitations = async () => {
    if (!currentTenantId) return;
    try {
      const pending = await tenantInvitationService.listPending(currentTenantId);
      setPendingTenantInvitations(pending);
    } catch (err) {
      console.error('Failed to fetch pending tenant invitations:', err);
    }
  };

  const fetchPendingAccessRequestCount = async () => {
    if (!currentTenantId) return;
    try {
      const pending = await addonAssignmentService.getAccessRequests(currentTenantId, 'pending');
      setPendingAccessRequestCount(pending.length);
    } catch {
      // Non-critical; leave count at 0
    }
  };

  const handleRevokeTenantInvitation = async (invitationId: string) => {
    if (!currentTenantId) return;
    try {
      await tenantInvitationService.revoke(invitationId, currentTenantId);
      await fetchPendingTenantInvitations();
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to revoke invitation');
    }
  };

  const handleToggleAddonSeat = async (userId: string, addonId: string, isAssigned: boolean) => {
    if (!currentTenantId) return;
    try {
      setSeatsActionLoading(true);
      if (isAssigned) {
        await addonAssignmentService.unassignSeat(currentTenantId, userId, addonId);
      } else {
        await addonAssignmentService.assignSeat(currentTenantId, userId, addonId);
      }
      const summary = await addonAssignmentService.getSeatSummary(currentTenantId);
      setSeatSummary(summary);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to update seat assignment');
    } finally {
      setSeatsActionLoading(false);
    }
  };

  const refreshSeatSummary = async () => {
    if (!currentTenantId) return;
    try {
      const summary = await addonAssignmentService.getSeatSummary(currentTenantId);
      setSeatSummary(summary);
    } catch (error: unknown) {
      console.error('Failed to refresh seat summary:', error);
    }
    // Refresh the badge count since an approval/denial changes pending count
    fetchPendingAccessRequestCount();
  };

  const handleOpenModal = (mode: ModalMode, user?: User) => {
    setModalMode(mode);
    setSelectedUser(user || null);
    setError(null);
    setSuccess(null);
    setShowAdminConfirmDialog(false);

    if (mode === 'edit' && user) {
      // If user is in the default tenant and has tenant-admin role, reset to 'user'
      // since tenant-admin is not a valid role for default tenant users
      const editRole = (user.tenantId === config.defaultTenantId && user.role === 'tenant-admin')
        ? 'user'
        : user.role;
      setFormData({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: editRole,
      });
    } else {
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: 'user',
      });
    }
  };

  const handleCloseModal = () => {
    setModalMode(null);
    setSelectedUser(null);
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      role: 'user',
    });
    setError(null);
    setSuccess(null);
    setShowAdminConfirmDialog(false);
    setSubmitting(false);
    if (modalMode === 'import') {
      setCsvRows([]);
    }
  };

  function parseCsvLine(line: string): string[] {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        const nextChar = line[i + 1];
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
        continue;
      }

      current += char;
    }

    values.push(current.trim());
    return values;
  }

  function parseCsv(text: string): CsvRow[] {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return [];
    const headers = parseCsvLine(lines[0]).map((h) => h.toLowerCase());
    const colIdx = (names: string[]) => names.reduce((found, n) => (found >= 0 ? found : headers.indexOf(n)), -1);
    const firstNameIdx = colIdx(['firstname', 'first_name', 'first name']);
    const lastNameIdx = colIdx(['lastname', 'last_name', 'last name']);
    const emailIdx = colIdx(['email']);
    const roleIdx = colIdx(['role']);
    if (emailIdx < 0) return [];
    return lines.slice(1).map((line) => {
      const cols = parseCsvLine(line);
      return {
        firstName: firstNameIdx >= 0 ? cols[firstNameIdx] ?? '' : '',
        lastName: lastNameIdx >= 0 ? cols[lastNameIdx] ?? '' : '',
        email: cols[emailIdx] ?? '',
        role: roleIdx >= 0 ? (cols[roleIdx] ?? 'user') : 'user',
      };
    }).filter((r) => r.email);
  }

  function handleCsvFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const parsed = parseCsv(text);
      setCsvRows(parsed.map((r) => ({ ...r, status: 'pending' })));
    };
    reader.readAsText(file);
    // Reset input so re-selecting same file fires onChange again
    e.target.value = '';
  }

  async function handleCsvImport() {
    if (!currentTenantId || csvRows.length === 0) return;
    setCsvImporting(true);
    const updated = [...csvRows];
    for (let i = 0; i < updated.length; i++) {
      const row = updated[i];
      if (row.status === 'success') continue;
      // Reject tenant-admin role for users in the default tenant
      if ((row.role || 'user') === 'tenant-admin' && currentTenantId === config.defaultTenantId) {
        updated[i] = { ...row, status: 'error', message: 'Tenant Admin role is not allowed in the default tenant' };
        setCsvRows([...updated]);
        continue;
      }
      try {
        const result = await userService.inviteUser(currentTenantId, {
          email: row.email,
          role: row.role || 'user',
          firstName: row.firstName,
          lastName: row.lastName,
        });
        const label = result.flow === 'existing_user' ? 'Invited (existing user)'
          : result.flow === 'existing_clerk_user' ? 'Added' : 'Invited';
        updated[i] = { ...row, status: 'success', message: label };
      } catch (error: unknown) {
        const apiError = getApiErrorInfo(error);
        const msg = apiError.message || 'Failed';
        updated[i] = { ...row, status: 'error', message: msg };
      }
      setCsvRows([...updated]);
    }
    setCsvImporting(false);
    const successCount = updated.filter((r) => r.status === 'success').length;
    if (successCount > 0) {
      track('user_invited', { role: 'user', count: successCount });
      fetchUsers();
    }
  }

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    // For edit operations, use the selected user's tenant; for invite, use the current tenant context
    const effectiveTenantId = (modalMode === 'edit' && selectedUser) ? selectedUser.tenantId : currentTenantId;
    if (!effectiveTenantId) return;

    try {
      setError(null);
      setSuccess(null);

      if (modalMode === 'edit') {
        if (!formData.firstName.trim()) {
          setError('First name is required.');
          return;
        }

        if (!formData.lastName.trim()) {
          setError('Last name is required.');
          return;
        }
      } else if (modalMode === 'invite') {
        if (!formData.email.trim()) {
          setError('Email is required.');
          return;
        }
        if (!formData.firstName.trim()) {
          setError('First name is required.');
          return;
        }
        if (!formData.lastName.trim()) {
          setError('Last name is required.');
          return;
        }
      }

      // Validation: Prevent tenant-admins from creating/updating users with 'admin' role
      if (!isSystemAdmin && formData.role === 'admin') {
        setError("You don't have permission to assign the System Admin role.");
        return;
      }

      // Validation: Prevent assigning tenant-admin role to users in the default tenant
      const targetTenantId = (modalMode === 'edit' && selectedUser) ? selectedUser.tenantId : effectiveTenantId;
      if (formData.role === 'tenant-admin' && targetTenantId === config.defaultTenantId) {
        setError('Users in the default tenant cannot be assigned the Tenant Admin role.');
        return;
      }

      // Confirm before promoting a user to system admin
      if (formData.role === 'admin' && selectedUser?.role !== 'admin' && !showAdminConfirmDialog) {
        setShowAdminConfirmDialog(true);
        return;
      }

      setSubmitting(true);

      if (modalMode === 'edit' && selectedUser) {
        await userService.updateUser(selectedUser.id, selectedUser.tenantId, {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          role: formData.role,
        });
        setShowAdminConfirmDialog(false);
        setSuccess('User updated successfully');
      } else if (modalMode === 'invite') {
        const email = formData.email.trim();
        const result = await userService.inviteUser(currentTenantId!, {
          email,
          role: formData.role,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
        });
        setShowAdminConfirmDialog(false);
        track('user_invited', { role: formData.role, count: 1 });
        if (result.flow === 'existing_user') {
          setSuccess(
            `Invitation sent to ${email}. This user already has a CPAx Academy account and will receive an email to confirm joining this organization.`
          );
        } else if (result.flow === 'existing_clerk_user') {
          setSuccess(
            `${email} has been added to your organization. They can log in immediately.`
          );
        } else {
          setSuccess(
            `Invitation sent to ${email}. They will appear in the list after accepting the invite and signing up.`
          );
        }
      }

      await fetchUsers();
      await fetchPendingTenantInvitations();
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('Error saving user:', error);
      setError(apiError.message || 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      setError(null);
      await userService.deleteUser(selectedUser.id, selectedUser.tenantId);
      setSuccess('User removed from organization successfully');
      await fetchUsers();
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('Error deleting user:', error);
      setError(apiError.message || 'Failed to remove user from organization');
    }
  };

  const handleResendInvite = async (user: User) => {
    try {
      setError(null);
      setSuccess(null);
      await userService.resendInvite(user.id, user.tenantId);
      setSuccess(`Invitation resent to ${user.email}.`);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('Error resending invite:', error);
      setError(apiError.message || 'Failed to resend invitation');
    }
  };

  const handleOpenMoveTenant = (user: User) => {
    setSelectedUser(user);
    setMoveTab('existing');
    setMoveTenantId('');
    setMoveRole('user');
    setNewTenantName('');
    setError(null);
    setSuccess(null);
    setModalMode('move-tenant');
  };

  const handleMoveToExistingTenant = async () => {
    if (!selectedUser || !moveTenantId) return;
    setMoveLoading(true);
    try {
      await userService.updateUserTenant(selectedUser.id, moveTenantId);
      if (moveRole !== selectedUser.role) {
        await userService.updateUser(selectedUser.id, moveTenantId, { role: moveRole });
      }
      setModalMode(null);
      setSuccess(`${selectedUser.firstName} ${selectedUser.lastName} moved to tenant successfully.`);
      fetchUsers();
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to move user.');
    } finally {
      setMoveLoading(false);
    }
  };

  const handleCreateTenantAndAssign = async () => {
    if (!selectedUser || !newTenantName.trim()) return;
    setMoveLoading(true);
    try {
      const newTenant = await tenantService.createTenant({ name: newTenantName.trim(), isActive: true });
      await userService.updateUserTenant(selectedUser.id, newTenant.id);
      await userService.updateUser(selectedUser.id, newTenant.id, { role: 'tenant-admin' });
      setModalMode(null);
      setSuccess(`Tenant "${newTenantName}" created. ${selectedUser.firstName} ${selectedUser.lastName} is now Tenant Admin.`);
      fetchUsers();
      tenantService.getAllTenants().then(setTenants).catch(() => {});
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to create tenant and assign user.');
    } finally {
      setMoveLoading(false);
    }
  };

  const userHasSeats = (userId: string) =>
    seatSummary.some(s => s.assignments.some(a => a.userId === userId));

  const stats = useMemo(() => {
    const activeUsers = users.filter(u => !!u.authProviderId).length;
    const pendingUsers = users.filter(u => !u.authProviderId).length;
    const invited = pendingUsers + pendingTenantInvitations.length;
    const total = activeUsers + invited;
    const withSeats = seatSummary.length > 0 ? users.filter(u => userHasSeats(u.id)).length : 0;
    const noSeats = seatSummary.length > 0 ? total - withSeats : 0;
    return { total, active: activeUsers, invited, withSeats, noSeats };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, seatSummary, pendingTenantInvitations]);

  const filteredEntries: TableEntry[] = useMemo(() => [
    ...users
      .filter((user) => {
        const term = searchTerm.trim().toLowerCase();
        const tenantName = tenants.find(t => t.id === user.tenantId)?.name ?? '';
        const matchesSearch =
          !term ||
          user.email.toLowerCase().includes(term) ||
          user.firstName.toLowerCase().includes(term) ||
          user.lastName.toLowerCase().includes(term) ||
          tenantName.toLowerCase().includes(term);
        if (!matchesSearch) return false;
        switch (statusFilter) {
          case 'active': return !!user.authProviderId;
          case 'invited': return !user.authProviderId;
          case 'with-seats': return userHasSeats(user.id);
          case 'no-seats': return !userHasSeats(user.id);
          default: return true;
        }
      })
      .map(user => ({ kind: 'user' as const, user })),
    ...(statusFilter === 'all' || statusFilter === 'invited'
      ? pendingTenantInvitations
          .filter(inv => {
            const term = searchTerm.trim().toLowerCase();
            return !term || inv.email.toLowerCase().includes(term);
          })
          .map(inv => ({ kind: 'invitation' as const, invitation: inv }))
      : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [users, pendingTenantInvitations, searchTerm, statusFilter, seatSummary, tenants]);

  const getSortValue = (entry: TableEntry, column: SortColumn): string => {
    if (entry.kind === 'user') {
      switch (column) {
        case 'name': return `${entry.user.firstName} ${entry.user.lastName}`.toLowerCase();
        case 'role': return isSystemWideView
          ? resolveUserType(entry.user, config.defaultTenantId)
          : entry.user.role;
        case 'status': return entry.user.authProviderId ? 'active' : 'invited';
        case 'created': return entry.user.createdAt;
      }
    } else {
      switch (column) {
        case 'name': return entry.invitation.email.toLowerCase();
        case 'role': return entry.invitation.role;
        case 'status': return 'invited';
        case 'created': return entry.invitation.createdAt;
      }
    }
  };

  const sortedEntries = useMemo(() => {
    return [...filteredEntries].sort((a, b) => {
      const aVal = getSortValue(a, sortColumn);
      const bVal = getSortValue(b, sortColumn);
      const cmp = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filteredEntries, sortColumn, sortDirection]);

  const paginatedEntries = sortedEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  if (userLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentTenantId && !isSystemWideView) {
    return (
      <Box>
        <Alert severity="warning">
          Unable to load user management. Please try refreshing the page or contact support if the problem persists.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page-level tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.95rem' },
        }}
      >
        <Tab label="Users" value="users" />
        <Tab
          label={
            <Badge badgeContent={pendingAccessRequestCount} color="warning" max={99}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pr: pendingAccessRequestCount > 0 ? 1.5 : 0 }}>
                {pendingAccessRequestCount > 0 && (
                  <PendingActionsIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                )}
                Access Requests
              </Box>
            </Badge>
          }
          value="requests"
        />
      </Tabs>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {activeTab === 'users' && (
      <>
      {/* Action toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {isSystemWideView
            ? 'Manage users and roles across all organizations.'
            : 'Manage users, roles, and module access for your organization.'}
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setModalMode('import')}
            disabled={!currentTenantId}
          >
            Import CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<MailIcon />}
            onClick={() => handleOpenModal('invite')}
            disabled={!currentTenantId}
          >
            Invite user
          </Button>
        </Stack>
      </Box>

      {/* Seat Usage Banner */}
      {seatSummary.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Module Seat Usage
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {seatSummary.map(s => (
              <Box key={s.addonId} sx={{ minWidth: 160 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{s.addonDisplayName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {s.assignedCount}/{s.purchasedQuantity}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={s.purchasedQuantity > 0 ? (s.assignedCount / s.purchasedQuantity) * 100 : 0}
                  color={s.availableCount === 0 ? 'error' : 'primary'}
                />
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Filter Tabs + Search */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <Tabs
          value={statusFilter}
          onChange={(_, v) => { setStatusFilter(v); setPage(0); }}
          sx={{
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            minHeight: 44,
            '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 500 },
          }}
        >
          <Tab label={`All (${stats.total})`} value="all" />
          <Tab label={`Active (${stats.active})`} value="active" />
          <Tab label={`Pending (${stats.invited})`} value="invited" />
          {seatSummary.length > 0 && <Tab label={`With access (${stats.withSeats})`} value="with-seats" />}
          {seatSummary.length > 0 && <Tab label={`No access (${stats.noSeats})`} value="no-seats" />}
        </Tabs>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={isSystemWideView ? "Search by name, email, or tenant…" : "Search by name or email…"}
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {/* Users Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}
      >
        <Table sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortColumn === 'name' ? sortDirection : false}>
                <TableSortLabel
                  active={sortColumn === 'name'}
                  direction={sortColumn === 'name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  User
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} sortDirection={sortColumn === 'role' ? sortDirection : false}>
                <TableSortLabel
                  active={sortColumn === 'role'}
                  direction={sortColumn === 'role' ? sortDirection : 'asc'}
                  onClick={() => handleSort('role')}
                >
                  {isSystemWideView ? 'User Type' : 'Role'}
                </TableSortLabel>
              </TableCell>
              {isSystemWideView && <TableCell>Tenant</TableCell>}
              <TableCell sortDirection={sortColumn === 'status' ? sortDirection : false}>
                <TableSortLabel
                  active={sortColumn === 'status'}
                  direction={sortColumn === 'status' ? sortDirection : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              {isSystemWideView && (
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Subscription</TableCell>
              )}
              {(isSystemWideView || seatSummary.length > 0) && (
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  Module Access
                </TableCell>
              )}
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} sortDirection={sortColumn === 'created' ? sortDirection : false}>
                <TableSortLabel
                  active={sortColumn === 'created'}
                  direction={sortColumn === 'created' ? sortDirection : 'asc'}
                  onClick={() => handleSort('created')}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedEntries.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    5
                    + (isSystemWideView ? 2 : 0)           // Tenant + Subscription columns
                    + (isSystemWideView || seatSummary.length > 0 ? 1 : 0) // Module Access
                  }
                  align="center"
                  sx={{ py: 8 }}
                >
                  <Typography color="text.secondary">
                    {searchTerm || statusFilter !== 'all'
                      ? 'No users match the current filters'
                      : 'No users yet'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedEntries.map((entry) => {
                if (entry.kind === 'invitation') {
                  const inv = entry.invitation;
                  return (
                    <TableRow key={`inv-${inv.id}`} hover>
                      <TableCell>
                        <Typography fontWeight={600} noWrap>
                          {inv.email}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip
                          label={inv.role === 'tenant-admin' ? 'Tenant Admin' : 'User'}
                          size="small"
                          color={inv.role === 'tenant-admin' ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      {isSystemWideView && <TableCell />}
                      <TableCell>
                        <Chip label="Invited" size="small" sx={subscriptionChipSx('warning')} />
                      </TableCell>
                      {isSystemWideView && <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }} />}
                      {(isSystemWideView || seatSummary.length > 0) && <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }} />}
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(inv.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Revoke invitation">
                          <IconButton onClick={() => handleRevokeTenantInvitation(inv.id)} color="error" size="small">
                            <PersonRemoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                }

                const user = entry.user;
                const tenantName = isSystemWideView
                  ? (tenants.find(t => t.id === user.tenantId)?.name ?? user.tenantId)
                  : null;
                const userType = resolveUserType(user, config.defaultTenantId);
                const subStatus = user.subscriptionStatus
                  ? SUBSCRIPTION_STATUS_MAP[user.subscriptionStatus] ?? { label: user.subscriptionStatus, tone: 'neutral' as const }
                  : null;
                return (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={600} noWrap>
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {user.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {isSystemWideView ? (
                        <Chip
                          label={USER_TYPE_LABELS[userType] ?? userType}
                          size="small"
                          color={USER_TYPE_COLORS[userType] ?? 'default'}
                          variant="outlined"
                          sx={{ height: 22, fontSize: 11, fontWeight: 500 }}
                        />
                      ) : (
                        <Chip
                          label={
                            user.role === 'admin'
                              ? 'System Admin'
                              : user.role === 'tenant-admin'
                              ? 'Tenant Admin'
                              : 'User'
                          }
                          size="small"
                          color={
                            user.role === 'admin'
                              ? 'warning'
                              : user.role === 'tenant-admin'
                              ? 'primary'
                              : 'default'
                          }
                          variant="outlined"
                          sx={{ height: 22, fontSize: 11, fontWeight: 500 }}
                        />
                      )}
                    </TableCell>
                    {isSystemWideView && (
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {tenantName}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={user.authProviderId ? 'Active' : 'Invited'}
                        size="small"
                        sx={subscriptionChipSx(user.authProviderId ? 'success' : 'warning')}
                      />
                    </TableCell>
                    {isSystemWideView && (
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                          {user.subscriptionPlanName ? (
                            <>
                              <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                {user.subscriptionPlanName}
                              </Typography>
                              {subStatus && (
                                <Chip
                                  label={subStatus.label}
                                  size="small"
                                  sx={[subscriptionChipSx(subStatus.tone), { height: 22, fontSize: 11, width: 'fit-content' }]}
                                />
                              )}
                            </>
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              No subscription
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    )}
                    {(isSystemWideView || seatSummary.length > 0) && (
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {isSystemWideView ? (
                          // System-wide view: show addons assigned to this user
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            {(user.assignedAddonIds ?? []).length > 0 ? (
                              (user.assignedAddonIds ?? []).map(addonId => (
                                <Chip
                                  key={addonId}
                                  label={ADDON_DISPLAY_NAMES[addonId] ?? addonId}
                                  size="small"
                                  color="primary"
                                  variant="filled"
                                  sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                                />
                              ))
                            ) : (
                              <Typography variant="caption" color="text.disabled">
                                None
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          // Tenant-scoped view: interactive seat assignment chips
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            {seatSummary.map(summary => {
                              const isAssigned = summary.assignments.some(a => a.userId === user.id);
                              const isFull = !isAssigned && summary.availableCount === 0;
                              const isLoading = seatsActionLoading;
                              return (
                                <Tooltip
                                  key={summary.addonId}
                                  title={
                                    isFull
                                      ? `No seats available for ${summary.addonDisplayName}`
                                      : isAssigned
                                      ? `Remove ${summary.addonDisplayName} seat`
                                      : `Assign ${summary.addonDisplayName} seat (${summary.availableCount} available)`
                                  }
                                >
                                  <span>
                                    <Chip
                                      label={summary.addonDisplayName}
                                      size="small"
                                      color={isAssigned ? 'primary' : 'default'}
                                      variant={isAssigned ? 'filled' : 'outlined'}
                                      icon={
                                        isFull
                                          ? <LockIcon style={{ fontSize: 13 }} />
                                          : isAssigned
                                          ? <RemoveCircleOutlineIcon style={{ fontSize: 13 }} />
                                          : <AddCircleOutlineIcon style={{ fontSize: 13 }} />
                                      }
                                      onClick={isFull || isLoading ? undefined : () => handleToggleAddonSeat(user.id, summary.addonId, isAssigned)}
                                      disabled={isLoading}
                                      sx={{
                                        height: 22,
                                        fontSize: 11,
                                        cursor: isFull ? 'default' : 'pointer',
                                        opacity: isFull ? 0.45 : 1,
                                        fontWeight: isAssigned ? 600 : 400,
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              );
                            })}
                          </Box>
                        )}
                      </TableCell>
                    )}
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {isSystemAdmin || user.role !== 'admin' ? (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          {!user.authProviderId && (
                            <Tooltip title="Resend invitation">
                              <IconButton onClick={() => handleResendInvite(user)} color="secondary" size="small">
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isSystemAdmin && (
                            <Tooltip title="Move to tenant">
                              <IconButton onClick={() => handleOpenMoveTenant(user)} color="default" size="small">
                                <SwapHorizIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit user">
                            <IconButton onClick={() => handleOpenModal('edit', user)} color="primary" size="small">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {backendProfile?.id !== user.id && (
                            <Tooltip title="Remove from organization">
                              <IconButton onClick={() => handleOpenModal('delete', user)} aria-label="Remove from organization" color="error" size="small">
                                <PersonRemoveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">
                          Protected
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {sortedEntries.length > rowsPerPage && (
        <TablePagination
          component="div"
          count={sortedEntries.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      )}

      {/* Result count */}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {sortedEntries.length === stats.total
          ? `${stats.total} users`
          : `${sortedEntries.length} of ${stats.total} users`}
      </Typography>
      </>
      )}

      {activeTab === 'requests' && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review and approve user requests for module access.
          </Typography>
          <AccessRequestsPanel embedded onApprovalSuccess={refreshSeatSummary} />
        </Box>
      )}

      {/* Modal */}
      <Dialog
        open={!!modalMode}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        {/* Modal Header */}
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            {modalMode === 'edit' && 'Edit User'}
            {modalMode === 'invite' && 'Invite User'}
            {modalMode === 'delete' && 'Remove from Organization'}
            {modalMode === 'move-tenant' && `Move ${selectedUser?.firstName} ${selectedUser?.lastName}`}
          </span>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Error/Success in Modal */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Remove Confirmation */}
          {modalMode === 'delete' && selectedUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography>
                Remove{' '}
                <strong>
                  {selectedUser.firstName} {selectedUser.lastName}
                </strong>{' '}
                from your organization?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Their account will remain active, allowing them to log in and view their CPE credit history. However, access to your organization’s resources will be removed.
              </Typography>
            </Box>
          )}

          {/* Move to Tenant */}
          {modalMode === 'move-tenant' && selectedUser && (
            <Box>
              <Tabs value={moveTab} onChange={(_, v) => setMoveTab(v)} sx={{ mb: 3 }}>
                <Tab label="Assign to Existing Tenant" value="existing" />
                <Tab label="Create New Tenant" value="new" icon={<AddBusinessIcon fontSize="small" />} iconPosition="start" />
              </Tabs>

              {moveTab === 'existing' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Choosing a tenant other than the user's current one will reassign them immediately.
                  </Alert>
                  <FormControl fullWidth>
                    <InputLabel>Target Tenant</InputLabel>
                    <Select
                      value={moveTenantId}
                      onChange={(e) => {
                        const newTenantId = e.target.value;
                        setMoveTenantId(newTenantId);
                        // Reset role if moving to default tenant and tenant-admin was selected
                        if (newTenantId === config.defaultTenantId && moveRole === 'tenant-admin') {
                          setMoveRole('user');
                        }
                      }}
                      label="Target Tenant"
                    >
                      {tenants
                        .filter(t => t.id !== selectedUser.tenantId)
                        .map(t => (
                          <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Role in New Tenant</InputLabel>
                    <Select
                      value={moveRole}
                      onChange={(e) => setMoveRole(e.target.value)}
                      label="Role in New Tenant"
                    >
                      <MenuItem value="user">User</MenuItem>
                      {moveTenantId !== config.defaultTenantId && (
                        <MenuItem value="tenant-admin">Tenant Admin</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {moveTab === 'new' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    A new tenant will be created and this user will become its Tenant Admin.
                  </Alert>
                  <TextField
                    label="New Tenant Name"
                    required
                    fullWidth
                    value={newTenantName}
                    onChange={(e) => setNewTenantName(e.target.value)}
                    placeholder="e.g. Acme Corp"
                    disabled={moveLoading}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Add/Edit/Invite Form */}
          {(modalMode === 'edit' || modalMode === 'invite') && !success && (
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Email Address"
                type="email"
                required
                fullWidth
                autoFocus={modalMode === 'invite'}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={modalMode === 'edit' || submitting}
                placeholder="user@example.com"
              />

              <TextField
                label="First Name"
                type="text"
                required
                fullWidth
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                disabled={submitting}
                placeholder="John"
              />

              <TextField
                label="Last Name"
                type="text"
                required
                fullWidth
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                disabled={submitting}
                placeholder="Doe"
              />

              <FormControl fullWidth disabled={submitting}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  label="Role"
                >
                  <MenuItem value="user">User</MenuItem>
                  {isSystemAdmin && <MenuItem value="admin">Admin</MenuItem>}
                  {((modalMode === 'edit' && selectedUser)
                    ? selectedUser.tenantId !== config.defaultTenantId
                    : !isDefaultTenant
                  ) && <MenuItem value="tenant-admin">Tenant Admin</MenuItem>}
                </Select>
              </FormControl>

              {modalMode === 'invite' && (
                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', p: 1.5, borderRadius: 1.5, bgcolor: 'tint.primarySoft' }}>
                  <MailIcon sx={{ fontSize: 18, color: 'primary.main', mt: '2px' }} />
                  <Typography variant="body2">
                    An invitation email will be sent. When they sign up, they'll be added to your organization with the selected role.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          {modalMode === 'move-tenant' ? (
            <>
              <Button onClick={handleCloseModal} variant="outlined" disabled={moveLoading}>Cancel</Button>
              {moveTab === 'existing' ? (
                <Button
                  variant="contained"
                  onClick={handleMoveToExistingTenant}
                  disabled={!moveTenantId || moveLoading}
                  startIcon={moveLoading ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />}
                >
                  Move User
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleCreateTenantAndAssign}
                  disabled={!newTenantName.trim() || moveLoading}
                  startIcon={moveLoading ? <CircularProgress size={16} color="inherit" /> : <AddBusinessIcon />}
                >
                  Create &amp; Assign
                </Button>
              )}
            </>
          ) : success ? (
            <Button onClick={handleCloseModal} variant="contained">
              Done
            </Button>
          ) : modalMode === 'delete' ? (
            <>
              <Button onClick={handleCloseModal} variant="outlined">
                Cancel
              </Button>
              <Button onClick={handleDelete} variant="contained" color="error">
                Remove user
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseModal} variant="outlined" disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {modalMode === 'edit' && (submitting ? 'Updating…' : 'Update User')}
                {modalMode === 'invite' && (submitting ? 'Sending…' : 'Send Invitation')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* System Admin Promotion Confirmation Dialog */}
      <Dialog
        open={showAdminConfirmDialog}
        onClose={() => setShowAdminConfirmDialog(false)}
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Promote to System Admin?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {selectedUser ? (
              <>You are about to grant <strong>{selectedUser.firstName} {selectedUser.lastName}</strong> ({selectedUser.email}) full System Admin privileges.</>
            ) : (
              <>You are about to invite <strong>{formData.email}</strong> with full System Admin privileges.</>
            )}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            System Admins have unrestricted access to all tenants, users, and platform settings. This action should only be performed intentionally.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setShowAdminConfirmDialog(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="warning"
          >
            Yes, Promote to System Admin
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={modalMode === 'import'} onClose={handleCloseModal} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Import Users from CSV
          <IconButton onClick={handleCloseModal} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'tint.primarySoft' }}>
            <Typography variant="body2" component="span">
              Upload a CSV file with columns: <strong>firstName, lastName, email, role</strong>.
              Each user will receive an invitation email.
            </Typography>{' '}
            <Button
              size="small"
              variant="text"
              sx={{ p: 0, minWidth: 0, textDecoration: 'underline' }}
              onClick={() => {
                const sample = 'firstName,lastName,email,role\nJane,Smith,jane@example.com,user\nJohn,Doe,john@example.com,user';
                const blob = new Blob([sample], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'import_template.csv';
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Download template
            </Button>
          </Box>

          <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} sx={{ mb: 2 }}>
            Choose CSV file
            <input type="file" hidden accept=".csv,text/csv" onChange={handleCsvFile} />
          </Button>

          {csvRows.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvRows.map((row, idx) => (
                    <TableRow key={idx} sx={{ bgcolor: row.status === 'success' ? 'status.completed.bg' : row.status === 'error' ? 'status.attention.bg' : 'inherit' }}>
                      <TableCell>{[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.role}</TableCell>
                      <TableCell align="center">
                        {row.status === 'pending' && <Chip label="Pending" size="small" variant="outlined" />}
                        {row.status === 'success' && <Chip icon={<CheckCircleIcon />} label="Invited" size="small" sx={subscriptionChipSx('success')} />}
                        {row.status === 'error' && (
                          <Tooltip title={row.message ?? 'Error'}>
                            <Chip icon={<ErrorIcon />} label="Failed" size="small" sx={subscriptionChipSx('error')} />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseModal} variant="outlined">
            {csvRows.some((r) => r.status === 'success') ? 'Close' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleCsvImport}
            disabled={csvRows.length === 0 || csvImporting || csvRows.every((r) => r.status === 'success')}
            startIcon={csvImporting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          >
            {csvImporting ? 'Importing…' : `Import ${csvRows.filter((r) => r.status === 'pending').length} users`}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
